import BaseController from "vamos/cockpit/base/controller/type/BaseController";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import { NavConfig } from "vamos/cockpit/base/handler/NavigationHandler";
import V4Context from "sap/ui/model/odata/v4/Context";
import VariantManagementService from "vamos/cockpit/base/services/VariantManagementService";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Request, RequestPosition } from "vamos/cockpit/model/odata/VamosModel";
import ListHandler from "vamos/cockpit/base/handler/ListHandler";
import Filter from "sap/ui/model/Filter";
import JSONFilterFieldDelegate from "vamos/cockpit/base/delegates/JSONFilterFieldDelegate";
import Sorter from "sap/ui/model/Sorter";
import Table, { Table$FilterEvent, Table$SortEvent } from "sap/ui/table/Table";
import { DatePicker$ChangeEvent } from "sap/m/DatePicker";
import MessageBox from "sap/m/MessageBox";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import formatter from "vamos/cockpit/base/formatter/formatter";
import XMLView from "sap/ui/core/mvc/XMLView";
import ViewHandler from "vamos/cockpit/base/handler/ViewHandler";
import RequestPositionDialogController from "vamos/cockpit/data/request/controller/RequestPositionDialog.controller";
import RequestService from "vamos/cockpit/data/request/service/RequestService";
import P13nService from "vamos/cockpit/base/services/P13nService";
import FilterOperator from "sap/ui/model/FilterOperator";
import { SearchField$SearchEvent } from "sap/m/SearchField";
import { Button$PressEvent } from "sap/m/Button";
import { RequestNumberInvoiceRequest } from "vamos/cockpit/model/api/vamos.private.api.types";
import PdfFileHandler from "vamos/cockpit/base/services/file/PdfFileHandler";
import { MultiSelectionPlugin$SelectionChangeEvent } from "sap/ui/table/plugins/MultiSelectionPlugin";

/**
 * @namespace vamos.cockpit.data.request.controller
 * */
export default class RequestDetailsController extends BaseController {
    public formatter: formatter = formatter;
    public requestService: RequestService = RequestService;
    public p13nService: P13nService = P13nService;
    public variantManagement: VariantManagementService = VariantManagementService;

    private variantsLoaded: Promise<JSONModel>;
    private requestDetailsOrderPositionsTable: Table;
    private listHandler: ListHandler;
    private _oDefaultVariantContext: any = {
        requestDetailsOrderPositionsTable: {
            conditions: {},
            sorters: {
                ArticleNumber: "Ascending",
            },
            filters: {},
            width: {
                MaterialGroupIcon: "2.5rem",
                VariantName: "20%",
            },
            states: {},
        },
    };
    private searchQuery: string;
    private pdfFileHandler: PdfFileHandler;

    onInit() {
        super.onInit();
        this.requestDetailsOrderPositionsTable = this.byId("requestDetailsOrderPositionsTable") as Table;
        this.getRouter().getRoute("requestDetails").attachPatternMatched((event: Route$PatternMatchedEvent) => {
            void this.handlePatternMatched(event).catch();
            return;
        }, this);
        this.variantsLoaded = VariantManagementService.initVariants.call(this);
        // set the tables model as early as in the other controllers, so the p13n button can apply the column states on afterInitialization
        void this.variantsLoaded.then((model: JSONModel) => {
            this.tableModel = model;
            this.getView().setModel(model, "tables");
        });
        this.attachDoubleClickEvent(this, this.requestDetailsOrderPositionsTable);
        this.pdfFileHandler = new PdfFileHandler(this);
    }

    async handlePatternMatched(event: Route$PatternMatchedEvent) {

        const args: object = event.getParameter("arguments");
        const navConfig = args as NavConfig;
        const path = decodeURIComponent(navConfig.path);
        const binding = this.getModel().bindContext(path, null, null);
        const context = binding.getBoundContext() as V4Context;
        this.getView().setBindingContext(context);
        const request = await context.requestObject() as Request;
        this.viewModel.setProperty("/viewTitle", this.getText("RequestEditTitle", [request.Number]));
        await this.bindRequestPositionTable(request.Guid);
    }

    private async bindRequestPositionTable(guid: string) {
        if (!guid) return;
        const model = await this.variantsLoaded;

        this.listHandler = new ListHandler(this.requestDetailsOrderPositionsTable, this.viewModel, model);
        this.listHandler.setCount(0);

        const filters: Filter | Filter[] = JSONFilterFieldDelegate.getFilterFromTableModel.call(this, model, this.requestDetailsOrderPositionsTable, "/RequestPositions");
        const filterParam = Array.isArray(filters) ? filters : [filters];
        const sorter: Sorter | undefined = JSONFilterFieldDelegate.getSorterFromTableModel.call(this, model, this.requestDetailsOrderPositionsTable);
        const params = { $filter: `RequestGuid eq '${guid}'` };
        this.listHandler.bindTable("RequestPosition", params, filterParam, sorter);
        this.requestDetailsOrderPositionsTable.setFirstVisibleRow(0);
    }

    public onChangeProductionDate(event: DatePicker$ChangeEvent) {
        const selectedDate: Date = event.getSource().getDateValue();
        const currentDate: Date = new Date();
        if (selectedDate < currentDate) {
            MessageBox.warning(this.getText("ProductionDateInPast"));
        }
    }

    public onSort(event: Table$SortEvent) {
        const table: Table = event.getSource();
        const tableId: string = this.getView().getLocalId(table.getId());
        const column = event.getParameter("column");
        const sortOrder = event.getParameter("sortOrder");
        const descending: boolean = sortOrder === "Descending";
        if (column && column.getSortProperty() === "Delivered") {
            event.preventDefault();
            this.tableModel.setProperty(`/${tableId}/sorters/`, { Delivered: sortOrder });
            const binding = table.getBinding("rows") as ODataListBinding;
            binding.sort([
                new Sorter("Delivered", descending),
                new Sorter("Ordered", descending),
                new Sorter("OnStock", !descending),
                new Sorter("Extracted", !descending),
                new Sorter("CurrentStock", !descending),
                new Sorter("ReservedAmount", descending),
            ]);
            VariantManagementService.markModified.call(this, tableId, true);
            return;
        }
        this.listHandler.onSort(event, this);
    }

    public onRequestPositionSelectionChange(event: MultiSelectionPlugin$SelectionChangeEvent) {
        this.listHandler.onSelectionChangePlugin(event);

        const context = event.getSource().getBindingContext();
        if (!context) {
            return;
        }
        const requestPosition = context.getObject() as RequestPosition;
        this.viewModel.setProperty("/isExtracted", requestPosition.Extracted);
    }

    public async openRequestPositionDialog(context?: V4Context) {
        const view: XMLView = await ViewHandler.getView("vamos.cockpit.data.request.view.RequestPositionDialog", this.getComponent());
        const controller: RequestPositionDialogController = view.getController() as RequestPositionDialogController;
        const requestContext = this.getView().getBindingContext() as V4Context;
        const request: Request = requestContext.getObject() as Request;

        controller.openDialog(context, request.Guid, () => {
            this.requestDetailsOrderPositionsTable.getBinding("rows").refresh();
            (this.getView().getBindingContext() as V4Context).refresh();
        });
    }

    public async onCreateNewRequestPosition() {
        await this.openRequestPositionDialog();
    }

    public async onEditRequestPosition() {
        const context = this.listHandler.getSelectedContext()?.[0] as V4Context;
        if (!context) {
            console.error("no context");
            return;
        }
        await this.openRequestPositionDialog(context);
    }

    public formatIcon(
        canceled: boolean, importProblems: boolean, productionDate: Date, requirementMade: boolean, extracted, orderPositionsCount: number, completePositionsCount: number,
        coveredPositionsCount: number, deliveredPositionsCount: number,
    ): any {
        const obj = this.resolveStatus(canceled, importProblems, productionDate, requirementMade, extracted, orderPositionsCount, completePositionsCount, coveredPositionsCount, deliveredPositionsCount);
        return obj.icon;
    }

    public formatColor(
        canceled: boolean, importProblems: boolean, productionDate: Date, requirementMade: boolean, extracted, orderPositionsCount: number, completePositionsCount: number,
        coveredPositionsCount: number, deliveredPositionsCount: number,
    ): any {
        const obj = this.resolveStatus(canceled, importProblems, productionDate, requirementMade, extracted, orderPositionsCount, completePositionsCount, coveredPositionsCount, deliveredPositionsCount);
        return obj.color;
    }

    public async printRequest(oEvent: Button$PressEvent) {
        const oSource = oEvent.getSource();
        const oContext = oSource.getBindingContext();
        const bIsStock = oSource.data("isStock") === "true";
        const oPayload: RequestNumberInvoiceRequest = {
            isStock: bIsStock,
            requestGuid: oContext.getProperty("Guid") as string,
        };
        const sText = bIsStock
            ? this.getText("PrintForProduction")
            : `${this.getText("Request")}_${oContext.getProperty("Number")}_${Date.now()}`;

        await this.pdfFileHandler.createFile("/pdf/request", oPayload, sText);
    }

    public async printDeliveryNote(oEvent : Button$PressEvent) {
        const oSource = oEvent.getSource();
        const oContext = oSource.getBindingContext();
        const bIsStock = oSource.data("isStock") === "true";
        const requestGuid = oContext.getProperty("Guid") as string;
        const sText = bIsStock
            ? this.getText("DeliveryNote")
            : `${this.getText("Request")}_${oContext.getProperty("Number")}_${Date.now()}`;

        await this.pdfFileHandler.createFile(`/pdf/delivery-note/${requestGuid}`, null, sText);
    }


    private resolveStatus(canceled: boolean, importProblems: boolean, productionDate: Date, requirementMade: boolean, extracted, orderPositionsCount: number, completePositionsCount: number,
                          coveredPositionsCount: number, deliveredPositionsCount: number) {

        let obj = {
            color: null,
            icon: null,
        };
        if (canceled) {
            // Storniert
            obj.icon = "sap-icon://cancel";
            obj.color = "red";
        } else if (extracted) {
            // Entnommen
            obj.icon = "sap-icon://bo-strategy-management";
            obj.color = "#1369AF";
        } else if (importProblems || productionDate === null) {
            obj.icon = "sap-icon://warning";
            obj.color = "#ffcc00";
        } else if (!requirementMade) {
            //Bedarfsanforderung
            obj.icon = "sap-icon://simulate";
            obj.color = "#1369AF";
        } else if (orderPositionsCount === 0 &&
            coveredPositionsCount !== completePositionsCount &&
            orderPositionsCount === 0
        ) {
            // Nicht bestellt#
            obj.icon = "sap-icon://product";
            obj.color = "#ffcc00";
        } else if (orderPositionsCount === 0 && coveredPositionsCount === completePositionsCount) {
            // Vollständig auf Lager
            obj.icon = "sap-icon://product";
            obj.color = "green";
        } else if (orderPositionsCount > 0 && deliveredPositionsCount === orderPositionsCount) {
            // Geliefert
            obj.icon = "sap-icon://shipping-status";
            obj.color = "green";
        } else if (orderPositionsCount > deliveredPositionsCount && deliveredPositionsCount > 0) {
            // Teillieferung
            obj.icon = "sap-icon://shipping-status";
            obj.color = "#ffcc00";

        } else {
            obj.icon = "sap-icon://shipping-status";
            obj.color = "red";
            // Nicht geliefert
        }
        return obj;
    }

    private async filterTable() {

        const binding = this.requestDetailsOrderPositionsTable.getBinding("rows") as ODataListBinding;
        const filters: Filter[] = [];
        const normalFilterKeys = ["ArticleNumber", "ArticleName", "VariantName", "BaseUnitName"];

        if (this.searchQuery) {
            const normalFilters: Filter[] = normalFilterKeys.map((filter: string): Filter => new Filter({
                    path: filter,
                    operator: FilterOperator.Contains,
                    value1: this.searchQuery,
                    caseSensitive: false,
                }),
            );

            filters.push(new Filter(normalFilters, false));
        }
        const model = await this.variantsLoaded;
        const jsonFilters: Filter | Filter[] = JSONFilterFieldDelegate.getFilterFromTableModel.call(this, model, this.requestDetailsOrderPositionsTable, "/RequestPositions");
        if (!Array.isArray(jsonFilters)) filters.push(jsonFilters);

        binding.filter(filters);
    }

    public async onSearch(event: SearchField$SearchEvent) {
        this.searchQuery = event.getParameter("query");
        await this.filterTable();
    }

    public async onFilter(event: Table$FilterEvent) {
        event.preventDefault();
        event.getParameter("column")?.setFilterValue(event.getParameter("value"));

        await this.filterTable();
    }
}